import { BehaviorSubject } from 'rxjs'

import { UserProfile, UserSnapshot } from '@ti/contracts'

import {
  UserDirectoryKind,
  UserStoreEvent,
  UserStoreEventType,
  UserStoreFreshnessOptions,
  UserStoreView,
} from './user-store.types'
import { UserStoreEventBus } from './user-store-events'

const PAGE_SIZE = 200
const IDS_BATCH_SIZE = 200
const DEFAULT_FRESHNESS_MAX_AGE_MS = 60_000
const MAX_AUTHORITATIVE_ATTEMPTS = 2

type ScopeViewState<TUser extends UserProfile> = {
  items$: BehaviorSubject<readonly TUser[]>
  byId$: BehaviorSubject<ReadonlyMap<string, TUser>>
  isLoaded$: BehaviorSubject<boolean>
  isFresh$: BehaviorSubject<boolean>
  isRefreshing$: BehaviorSubject<boolean>
  view?: UserStoreView<TUser>
}

type ScopeState<TUser extends UserProfile> = {
  key: string
  users: Map<string, TUser>
  updatedAtById: Map<string, number>
  viewState: ScopeViewState<TUser>
  loadPromise?: Promise<void>
  refreshPromise?: Promise<void>
  revision: number
  activeOperations: number
  lastValidatedAt: number | null
}

export abstract class BaseScopedUsersStore<TUser extends UserProfile, TScope = void> {
  protected readonly maxScopes = Number.POSITIVE_INFINITY

  private readonly states = new Map<string, ScopeState<TUser>>()
  // Lightweight views intentionally outlive evicted directory data so existing callers can reload.
  private readonly viewStates = new Map<string, ScopeViewState<TUser>>()

  public view(scope: TScope): UserStoreView<TUser> {
    const key = this.scopeKey(scope)
    const viewState = this.getOrCreateViewState(key)
    this.getOrCreateState(scope)

    if (!viewState.view) {
      viewState.view = {
        items$: viewState.items$.asObservable(),
        byId$: viewState.byId$.asObservable(),
        isLoaded$: viewState.isLoaded$.asObservable(),
        isFresh$: viewState.isFresh$.asObservable(),
        isRefreshing$: viewState.isRefreshing$.asObservable(),
        ensureLoaded: () => this.ensureLoaded(scope),
        revalidateIfStale: (options) => this.revalidateIfStale(scope, options),
        ensureFresh: (options) => this.ensureFresh(scope, options),
        refresh: () => this.refresh(scope),
        refreshByIds: (ids) => this.refreshByIds(scope, ids),
        getById: (id) => this.states.get(key)?.users.get(id),
        getAll: () => Array.from(this.states.get(key)?.users.values() ?? []),
        isLoaded: () => viewState.isLoaded$.value,
        isFresh: () => viewState.isFresh$.value,
        isRefreshing: () => viewState.isRefreshing$.value,
      }
    }
    return viewState.view
  }

  public merge(scope: TScope, users: readonly TUser[]): void {
    if (!users.length) {
      return
    }

    const state = this.getOrCreateState(scope)
    const merged = new Map(state.users)
    const updatedAtById = new Map(state.updatedAtById)
    users.forEach((user) => {
      merged.set(user.id, user)
      updatedAtById.set(user.id, this.updatedAtValue(user.updatedAt))
    })
    this.commit(state, merged, updatedAtById)
  }

  protected abstract scopeKey(scope: TScope): string

  protected abstract loadPage(scope: TScope, page: number, pageSize: number): Promise<TUser[]>

  protected abstract loadByIds(scope: TScope, ids: string[]): Promise<TUser[]>

  protected abstract loadSnapshot(scope: TScope): Promise<UserSnapshot[]>

  protected connectEvents(
    eventBus: UserStoreEventBus,
    directory: UserDirectoryKind,
    resolveScope: (event: UserStoreEvent) => { scope: TScope } | undefined,
  ): void {
    eventBus.events$.subscribe((event) => {
      if (event.directory !== directory) {
        return
      }
      const resolved = resolveScope(event)
      if (!resolved || !this.hasState(resolved.scope)) {
        return
      }

      switch (event.type) {
        case UserStoreEventType.Created:
          this.merge(resolved.scope, [event.user as TUser])
          break
        case UserStoreEventType.Updated:
          if (event.user) {
            this.merge(resolved.scope, [event.user as TUser])
          } else {
            this.refreshByIds(resolved.scope, [event.userId]).catch((error) =>
              console.error(`Cannot refresh ${directory} user`, error),
            )
          }
          break
        case UserStoreEventType.Deleted:
          this.remove(resolved.scope, [event.userId])
          break
        case UserStoreEventType.Invalidate:
          this.invalidate(resolved.scope)
          break
      }
    })
  }

  protected hasState(scope: TScope): boolean {
    return this.states.has(this.scopeKey(scope))
  }

  private ensureLoaded(scope: TScope): Promise<void> {
    const state = this.getOrCreateState(scope)
    if (state.viewState.isLoaded$.value) {
      return Promise.resolve()
    }
    if (state.loadPromise) {
      return state.loadPromise
    }

    this.beginOperation(state)
    state.loadPromise = this.loadCompleteDirectory(scope, state).finally(() => {
      this.endOperation(state)
      state.loadPromise = undefined
    })
    return state.loadPromise
  }

  private async ensureFresh(scope: TScope, options: UserStoreFreshnessOptions = {}): Promise<void> {
    const state = this.getOrCreateState(scope)
    if (!state.viewState.isLoaded$.value) {
      await this.ensureLoaded(scope)
      return
    }

    if (!this.isStale(state, options)) {
      return
    }

    state.viewState.isFresh$.next(false)
    await this.refresh(scope)
  }

  private async revalidateIfStale(
    scope: TScope,
    options: UserStoreFreshnessOptions = {},
  ): Promise<void> {
    const state = this.getOrCreateState(scope)
    if (!state.viewState.isLoaded$.value) {
      await this.ensureLoaded(scope)
      return
    }

    if (!this.isStale(state, options)) {
      return
    }

    state.viewState.isFresh$.next(false)
    this.scheduleRefresh(scope)
  }

  private refresh(scope: TScope): Promise<void> {
    const state = this.getOrCreateState(scope)
    if (!state.viewState.isLoaded$.value) {
      return this.ensureLoaded(scope)
    }
    if (state.refreshPromise) {
      return state.refreshPromise
    }

    state.viewState.isFresh$.next(false)
    this.beginOperation(state)
    state.refreshPromise = this.reconcileSnapshot(scope, state).finally(() => {
      this.endOperation(state)
      state.refreshPromise = undefined
    })
    return state.refreshPromise
  }

  private async loadCompleteDirectory(scope: TScope, state: ScopeState<TUser>): Promise<void> {
    for (let attempt = 0; attempt < MAX_AUTHORITATIVE_ATTEMPTS; attempt += 1) {
      const revision = state.revision
      const users = await this.loadAllPages(scope)
      if (revision !== state.revision) {
        continue
      }

      this.commit(
        state,
        new Map(users.map((user) => [user.id, user])),
        new Map(users.map((user) => [user.id, this.updatedAtValue(user.updatedAt)])),
      )
      state.viewState.isLoaded$.next(true)
      this.markValidated(state)
      return
    }

    throw new Error(`User directory changed while loading scope ${state.key}`)
  }

  private async reconcileSnapshot(scope: TScope, state: ScopeState<TUser>): Promise<void> {
    for (let attempt = 0; attempt < MAX_AUTHORITATIVE_ATTEMPTS; attempt += 1) {
      const revision = state.revision
      const snapshot = await this.loadSnapshot(scope)
      const snapshotById = new Map(snapshot.map((item) => [item.id, item]))
      const idsToReload = snapshot
        .filter((item) => state.updatedAtById.get(item.id) !== this.updatedAtValue(item.updatedAt))
        .map((item) => item.id)
      const loadedUsers = await this.loadIds(scope, idsToReload)
      if (revision !== state.revision) {
        continue
      }

      const reconciled = new Map(
        Array.from(state.users.entries()).filter(([id]) => snapshotById.has(id)),
      )
      const updatedAtById = new Map(
        Array.from(state.updatedAtById.entries()).filter(([id]) => snapshotById.has(id)),
      )
      idsToReload.forEach((id) => {
        reconciled.delete(id)
        updatedAtById.delete(id)
      })
      loadedUsers.forEach((user) => {
        reconciled.set(user.id, user)
        updatedAtById.set(user.id, this.updatedAtValue(user.updatedAt))
      })
      this.commit(state, reconciled, updatedAtById)
      this.markValidated(state)
      return
    }

    throw new Error(`User directory changed while refreshing scope ${state.key}`)
  }

  private async refreshByIds(scope: TScope, ids: string[]): Promise<void> {
    const uniqueIds = Array.from(new Set(ids.filter(Boolean)))
    if (!uniqueIds.length) {
      return
    }

    const state = this.getOrCreateState(scope)
    this.beginOperation(state)
    try {
      for (let attempt = 0; attempt < MAX_AUTHORITATIVE_ATTEMPTS; attempt += 1) {
        const revision = state.revision
        const users = await this.loadIds(scope, uniqueIds)
        if (revision !== state.revision) {
          continue
        }

        const refreshed = new Map(state.users)
        const updatedAtById = new Map(state.updatedAtById)
        uniqueIds.forEach((id) => {
          refreshed.delete(id)
          updatedAtById.delete(id)
        })
        users.forEach((user) => {
          refreshed.set(user.id, user)
          updatedAtById.set(user.id, this.updatedAtValue(user.updatedAt))
        })
        this.commit(state, refreshed, updatedAtById)
        return
      }

      throw new Error(`User directory changed while refreshing IDs for scope ${state.key}`)
    } finally {
      this.endOperation(state)
    }
  }

  private async loadAllPages(scope: TScope): Promise<TUser[]> {
    const users: TUser[] = []
    for (let page = 0; ; page += 1) {
      const pageUsers = await this.loadPage(scope, page, PAGE_SIZE)
      users.push(...pageUsers)
      if (pageUsers.length < PAGE_SIZE) {
        return users
      }
    }
  }

  private async loadIds(scope: TScope, ids: string[]): Promise<TUser[]> {
    const users: TUser[] = []
    for (let index = 0; index < ids.length; index += IDS_BATCH_SIZE) {
      const idsChunk = ids.slice(index, index + IDS_BATCH_SIZE)
      users.push(...(await this.loadByIds(scope, idsChunk)))
    }
    return users
  }

  private remove(scope: TScope, ids: string[]): void {
    const state = this.states.get(this.scopeKey(scope))
    if (!state) {
      return
    }
    const users = new Map(state.users)
    const updatedAtById = new Map(state.updatedAtById)
    ids.forEach((id) => {
      users.delete(id)
      updatedAtById.delete(id)
    })
    this.commit(state, users, updatedAtById)
  }

  private invalidate(scope: TScope): void {
    const state = this.states.get(this.scopeKey(scope))
    if (!state) {
      return
    }

    state.revision += 1
    state.lastValidatedAt = null
    state.viewState.isFresh$.next(false)
    this.scheduleRefresh(scope)
  }

  private scheduleRefresh(scope: TScope): void {
    const state = this.getOrCreateState(scope)
    const refresh = state.viewState.isLoaded$.value ? this.refresh(scope) : this.ensureLoaded(scope)
    refresh.catch((error) => console.error(`Cannot refresh user directory ${state.key}`, error))
  }

  private commit(
    state: ScopeState<TUser>,
    users: Map<string, TUser>,
    updatedAtById: Map<string, number>,
  ): void {
    state.revision += 1
    state.users = users
    state.updatedAtById = updatedAtById
    state.viewState.items$.next(Array.from(users.values()))
    state.viewState.byId$.next(new Map(users))
  }

  private markValidated(state: ScopeState<TUser>): void {
    state.lastValidatedAt = this.now()
    state.viewState.isFresh$.next(true)
  }

  private getOrCreateViewState(key: string): ScopeViewState<TUser> {
    const existing = this.viewStates.get(key)
    if (existing) {
      return existing
    }

    const viewState: ScopeViewState<TUser> = {
      items$: new BehaviorSubject<readonly TUser[]>([]),
      byId$: new BehaviorSubject<ReadonlyMap<string, TUser>>(new Map()),
      isLoaded$: new BehaviorSubject(false),
      isFresh$: new BehaviorSubject(false),
      isRefreshing$: new BehaviorSubject(false),
    }
    this.viewStates.set(key, viewState)
    return viewState
  }

  private getOrCreateState(scope: TScope): ScopeState<TUser> {
    const key = this.scopeKey(scope)
    const existing = this.states.get(key)
    if (existing) {
      this.states.delete(key)
      this.states.set(key, existing)
      return existing
    }

    const state: ScopeState<TUser> = {
      key,
      users: new Map(),
      updatedAtById: new Map(),
      viewState: this.getOrCreateViewState(key),
      revision: 0,
      activeOperations: 0,
      lastValidatedAt: null,
    }
    this.states.set(key, state)
    this.evictScopes(key)
    return state
  }

  private evictScopes(protectedKey?: string): void {
    while (this.states.size > this.maxScopes) {
      const candidate = Array.from(this.states.entries()).find(
        ([key, state]) =>
          key !== protectedKey &&
          !state.loadPromise &&
          !state.refreshPromise &&
          state.activeOperations === 0,
      )
      if (!candidate) {
        return
      }

      const [key, state] = candidate
      state.viewState.items$.next([])
      state.viewState.byId$.next(new Map())
      state.viewState.isLoaded$.next(false)
      state.viewState.isFresh$.next(false)
      state.viewState.isRefreshing$.next(false)
      this.states.delete(key)
    }
  }

  private isStale(state: ScopeState<TUser>, options: UserStoreFreshnessOptions): boolean {
    const maxAgeMs = options.maxAgeMs ?? DEFAULT_FRESHNESS_MAX_AGE_MS
    return (
      !state.viewState.isFresh$.value ||
      state.lastValidatedAt === null ||
      this.now() - state.lastValidatedAt > maxAgeMs
    )
  }

  private updatedAtValue(value: Date | string): number {
    return value instanceof Date ? value.valueOf() : new Date(value).valueOf()
  }

  private now(): number {
    return Date.now()
  }

  private beginOperation(state: ScopeState<TUser>): void {
    state.activeOperations += 1
    if (state.activeOperations === 1) {
      state.viewState.isRefreshing$.next(true)
    }
  }

  private endOperation(state: ScopeState<TUser>): void {
    state.activeOperations = Math.max(0, state.activeOperations - 1)
    if (state.activeOperations === 0) {
      state.viewState.isRefreshing$.next(false)
      this.evictScopes()
    }
  }
}
